import type { DiaRuta } from '../../hooks/useFiltrosAgendamiento'

/**
 * B.2 · Un color estable por dia de la semana, como MODULO COMPARTIDO.
 *
 * El calendario semanal (B.1), el desplegable de productos+dia (B.3) y la tabla
 * de clientes tinen el mismo dia con el mismo color. Que ese mapeo viva en un
 * solo sitio es justo el punto: si el lunes es azul en el calendario tiene que
 * ser azul en todas partes, y la unica forma de garantizarlo es que nadie
 * vuelva a elegir el color a mano. Por eso esto no pinta nada -no es un
 * componente-, solo dice que color le toca a cada dia.
 *
 * ESTABILIDAD (lo que pide la spec)
 *
 * El mapeo es un `Record` constante indexado por `r1..r5`, no una funcion que
 * dependa de la fecha ni de la semana. En consecuencia el martes es **siempre**
 * el mismo color, esta semana y la que viene, aqui y en cualquier vista que
 * importe este modulo. `colorDeFecha` traduce una fecha real a su dia de ruta,
 * de modo que dos martes distintos caen en la misma entrada.
 *
 * POR QUE ESTOS TOKENS Y NO COLORES NUEVOS
 *
 * No se inventa ni un hex ni un token: se reutilizan cinco TRIOS de la paleta
 * corporativa que `index.css` ya declara con `light-dark()` y que estan medidos
 * contra WCAG en los dos temas (fondo / tinta / anillo). Asi cada dia nace
 * legible en claro y en oscuro sin anadir una sola variable a la hoja -que
 * ademas es un fichero que esta tarea no debe tocar-.
 *
 *   r1 lunes     -> acento    (azul corporativo; abre la semana con la marca)
 *   r2 martes    -> positivo  (verde)
 *   r3 miercoles -> avisado   (cian / azul cielo)
 *   r4 jueves    -> atencion  (ambar)
 *   r5 viernes   -> negativo  (rosa)
 *
 * Son cinco tonos repartidos por la rueda -azul, verde, cian, ambar, rosa- para
 * que se distingan de un vistazo en una cuadricula de dias. `index.css` reserva
 * el ambar y el rojo para "semanas sin pedir", y aqui se usa el ambar para el
 * jueves a sabiendas: el color NUNCA lleva el dato solo -misma regla que las
 * insignias del panel-, el nombre del dia acompana siempre al tinte, y el
 * contexto (una cuadricula de dias, no la columna de avisos) desambigua. Sin
 * esa concesion no habria cinco hues distintos, que es justo lo que pide B.2.
 */

/** Relleno, tinta y anillo de un dia. Tres utilidades Tailwind ya validadas. */
export interface ColorDia {
  clave: DiaRuta
  /** El nombre del dia. El color no viaja nunca sin el. */
  etiqueta: string
  /** Utilidad de fondo (`bg-...`). */
  fondo: string
  /** Utilidad de tinta (`text-...`), con contraste medido sobre `fondo`. */
  tinta: string
  /** Utilidad de anillo (`ring-...`); pide un `ring-1` en el consumidor. */
  anillo: string
  /** Las tres juntas, listas para el `className` de una celda o pastilla. */
  clase: string
}

function crear(clave: DiaRuta, etiqueta: string, familia: {
  fondo: string
  tinta: string
  anillo: string
}): ColorDia {
  return {
    clave,
    etiqueta,
    fondo: familia.fondo,
    tinta: familia.tinta,
    anillo: familia.anillo,
    clase: `${familia.fondo} ${familia.tinta} ${familia.anillo}`,
  }
}

/**
 * El mapeo estable. `as const` en las claves para que TypeScript sepa que estan
 * las cinco y solo las cinco: si algun dia se anade `r6` al tipo `DiaRuta`, esto
 * deja de compilar hasta que alguien le elija color, que es lo que se quiere.
 */
export const COLORES_DIA: Record<DiaRuta, ColorDia> = {
  // acento no tiene el trio fondo/tinta/anillo: usa su variante suave, su tinta
  // y su borde, que cumplen el mismo papel y estan igual de validados.
  r1: crear('r1', 'Lunes', {
    fondo: 'bg-acento-suave',
    tinta: 'text-acento-tinta',
    anillo: 'ring-acento-borde',
  }),
  r2: crear('r2', 'Martes', {
    fondo: 'bg-positivo-fondo',
    tinta: 'text-positivo-tinta',
    anillo: 'ring-positivo-anillo',
  }),
  r3: crear('r3', 'Miercoles', {
    fondo: 'bg-avisado-fondo',
    tinta: 'text-avisado-tinta',
    anillo: 'ring-avisado-anillo',
  }),
  r4: crear('r4', 'Jueves', {
    fondo: 'bg-atencion-fondo',
    tinta: 'text-atencion-tinta',
    anillo: 'ring-atencion-anillo',
  }),
  r5: crear('r5', 'Viernes', {
    fondo: 'bg-negativo-fondo',
    tinta: 'text-negativo-tinta',
    anillo: 'ring-negativo-anillo',
  }),
}

/** En orden de lunes a viernes, para pintar una fila de dias sin ordenar a mano. */
export const COLORES_DIA_LISTA: readonly ColorDia[] = [
  COLORES_DIA.r1,
  COLORES_DIA.r2,
  COLORES_DIA.r3,
  COLORES_DIA.r4,
  COLORES_DIA.r5,
]

/** El color de un dia de ruta. Total sobre `DiaRuta`: siempre devuelve uno. */
export function colorDeDia(dia: DiaRuta): ColorDia {
  return COLORES_DIA[dia]
}

/**
 * De una fecha real a su color, o `null` en fin de semana.
 *
 * `getDay()` da 0=domingo … 6=sabado en la zona local; el reparto va de lunes a
 * viernes, asi que sabado y domingo no tienen dia de ruta y devuelven `null` en
 * vez de inventarse uno. Se usa la hora **local** a proposito: el dia del
 * reparto es el del calendario de quien mira la pantalla, no el UTC, igual que
 * razona `formato.ts` al formatear fechas sin hora.
 */
export function colorDeFecha(fecha: Date): ColorDia | null {
  const dia = fecha.getDay()
  if (dia < 1 || dia > 5) return null
  const clave = `r${dia}` as DiaRuta
  return COLORES_DIA[clave]
}
